<template>
    <div
        class="bo-return-search"
        id="bo-lucene-search"
        v-click-outside="vcoConfig"
    >
        <!-- SEARCH DROPDOWN -->
        <div class="searchbar rounded">
            <div
                class="search-selection"
                :class="{ disabled: isHomePage }"
                id="toggleButton"
                @click="toggleCategoryFilter"
            >
                <span>
                    {{ buttonText }}
                </span>
                <i class="icon-signalcellular4bar"></i>
            </div>
            <a
                class="btn-search"
                id="consumer_search_product_link"
            >
                <i
                    class="icon-search"
                    @click="searchIfNeeded"
                ></i>
            </a>
            <input
                ref="searchInput"
                type="text"
                id="dropdownSearchBar"
                data-toggle="dropdown"
                data-display="static"
                aria-haspopup="true"
                aria-expanded="false"
                class="search-input"
                :placeholder="generalTranslations?.search ?? 'Search'"
                v-model="searchValue"
                @input="reopenDropdown"
                @keypress="checkKeyPress"
                @focus="updateDropdownWidth"
            />
            <div
                v-if="searchValue"
                class="search-clear"
            >
                <i
                    class="icon-close"
                    @click="searchValue = ''"
                ></i>
            </div>
            <div
                v-if="showLoadingIcon"
                class="loading-indicator"
            >
                <div class="spinner"></div>
            </div>
            <div
                class="dropdown-menu dropdown-search-menu included"
                aria-labelledby="dropdownSearchBar"
                ref="dropdownSearchBar"
                :style="{ width: dropdownSearchBarWidth }"
            >
                <div class="dropdown-search-filter">
                    <button
                        v-for="field in searchFields"
                        :key="field"
                        class="btn-rounded filterBtn included"
                        :class="isSearchFieldSelected(field) ? 'filter-select' : 'filter-unselect'"
                        @click="filterControl(field)"
                    >
                        {{ searchFieldLabel(field) }}
                    </button>
                </div>
                <bo-search-suggestions
                    v-if="searchValue.trim() && !leavingForResults"
                    :query="searchValue.trim()"
                    :words="searchWords"
                    :show-products="productFields.length > 0"
                    :show-categories="searchCategories"
                    :show-description="isSearchFieldSelected('description')"
                    :products="productResults"
                    :product-total="productTotal"
                    :products-loading="productsLoading"
                    :categories="categoryResults"
                    :category-total="categoryTotal"
                    :categories-loading="categoriesLoading"
                    :results-current="resultsQuery === searchValue.trim()"
                    :pin-view-all="true"
                    :header-translations="headerTranslations"
                    @select-product="clickProduct"
                    @select-category="clickCategory"
                    @view-all="goToSearchResults"
                ></bo-search-suggestions>
                <bo-search-history
                    v-else
                    :terms="searchHistory"
                    :editing="editingSearchHistory"
                    :max-rows="2"
                    :translations="headerTranslations"
                    @select="searchHistoryTerm"
                    @remove="removeSearchHistoryTerm"
                    @clear-all="clearSearchHistory"
                    @edit="editingSearchHistory = $event"
                ></bo-search-history>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";

Vue.component("bo-toast", require("@/v2/components/bo-toast.vue").default);
Vue.component("bo-search-suggestions", require("js/frontend/components/bo-search-suggestions.vue").default);
Vue.component("bo-search-history", require("js/frontend/components/bo-search-history.vue").default);

import productMixin from "@/v2/mixins/products.js";
import { searchPreferenceMixin } from "js/frontend/mixins/search-preference.js";

export default {
    mixins: [productMixin, searchPreferenceMixin],

    props: {
        productsUrl: {
            type: String,
            default: null,
        },
        searchResultUrl: {
            type: String,
            default: null,
        },
        homepageUrl: {
            type: String,
            default: null,
        },
        outletId: {
            type: String,
            default: null,
        },
        categoryId: {
            type: String,
            default: null,
        },
        generalTranslations: {
            type: Object,
        },
        headerTranslations: {
            type: Object,
        },
    },

    data() {
        return {
            searchValue: "",
            productResults: [],
            productTotal: 0,
            productsLoading: false,
            categoryResults: [],
            categoryTotal: 0,
            categoriesLoading: false,
            // the query the shown results belong to
            resultsQuery: "",
            searchCategory: false,
            showSearch: false,
            vcoConfig: {
                handler: this.deactivateIfNeeded,
                middleware: this.ignoreLoadingElement,
            },
            showResult: false,
            isHomePage: true,
            dropdownSearchBarWidth: null,
            newCategoryId: null,
        };
    },

    mounted() {
        this.updateDropdownWidth();
        window.addEventListener("resize", this.updateDropdownWidth);
        document.addEventListener("click", this.closeDropdownIfClickedOutside);

        this.$refs.dropdownSearchBar.addEventListener("click", (event) => {
            event.stopPropagation();
        });

        this.newCategoryId = this.categoryId;
        this.checkUrlString();

        if (!window.EventBus) {
            window.EventBus = new Vue();
        }
        window.EventBus.$on("category-id", (categoryId) => (this.newCategoryId = categoryId));
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.updateDropdownWidth);
    },

    watch: {
        searchValue(newValue, oldValue) {
            if (this.leavingForResults) return;

            if (newValue.trim() != "") {
                if (newValue !== oldValue) {
                    setTimeout(() => {
                        if (this.searchValue === newValue) {
                            this.searchIfNeeded();
                        }
                    }, 1000);
                }
            }
        },
    },

    computed: {
        buttonText() {
            return this.searchCategory
                ? this.headerTranslations?.category ?? "Category"
                : this.headerTranslations?.global ?? "Global";
        },

        showLoadingIcon() {
            return this.productsLoading || this.categoriesLoading || this.leavingForResults;
        },
    },

    methods: {
        updateDropdownWidth() {
            this.$nextTick(() => {
                this.dropdownSearchBarWidth = document.getElementById("bo-lucene-search").offsetWidth + "px";
            });
        },

        filterControl(field) {
            if (this.toggleSearchField(field)) {
                this.searchIfNeeded();
            }
        },

        checkUrlString() {
            const catalogPattern = new RegExp(`^${this.homepageUrl}/\\w+/catalog/category`);

            //searching from homepage will not capture category ID in url
            const categoryPattern = new RegExp(`^${this.homepageUrl}/\\w+/catalog/\\w+/categoryId`);

            if (catalogPattern.test(window.location.href) || categoryPattern.test(window.location.href)) {
                this.isHomePage = false;
            }
        },

        searchIfNeeded() {
            if (this.searchValue.trim() != "") {
                this.searchProductWithKeys();
            }
        },

        // products and categories load in parallel; each section renders as soon as its own response arrives
        searchProductWithKeys() {
            const search = this.searchValue.trim();
            const isCurrentSearch = () => search === this.searchValue.trim();
            this.resultsQuery = search;

            this.productsLoading = this.productFields.length > 0;
            this.categoriesLoading = this.searchCategories;

            if (this.productsLoading) {
                let payload = { params: {} };
                payload["params"]["luceneSearchString"] = search;
                payload["params"]["luceneSearchField"] = this.productFields;
                payload["params"]["limit"] = 6;
                payload["params"]["categoryId"] = this.newCategoryId;
                payload["params"]["isCategorySearch"] = this.searchCategory;
                payload["params"]["sort[name]"] = "asc";
                payload["params"]["status"] = "publish";

                const finish = (products = [], meta = null) => {
                    if (!isCurrentSearch()) return;
                    this.productResults = this.convertProductToSearchResult(products);
                    this.productTotal = meta?.total ?? this.productResults.length;
                    this.productsLoading = false;
                };
                this.getProducts(this.productsUrl, payload, finish, () => finish(), false);
            }

            if (this.categoriesLoading) {
                this.searchCategoryEntries(
                    {
                        search,
                        limit: 6,
                        outletId: this.outletId,
                        categoryId: this.searchCategory ? this.newCategoryId : null,
                    },
                    (categories, total) => {
                        if (!isCurrentSearch()) return;
                        this.categoryResults = categories;
                        this.categoryTotal = total;
                        this.categoriesLoading = false;
                    }
                );
            }
        },

        convertProductToSearchResult(products) {
            return Object.values(products)
                .filter((product) => product.status == "publish")
                .map((product) => {
                    var imagePath = "";
                    Object.values(product.images).forEach((details) => {
                        imagePath = details.source_url_small;
                    });

                    return {
                        id: product.id,
                        image: imagePath ? imagePath : "/assets/graphics/img-product.svg",
                        name: product.name,
                        sku: product.sku,
                        barcode: product.barcode,
                        description: product.description,
                    };
                });
        },

        async clickProduct(product) {
            await Promise.all([this.waitForSearchPreferenceSave(), this.recordSearch(this.searchValue)]);
            window.location.href = `${this.homepageUrl}/${this.outletId}/product_details/${product.id}`;
        },

        async clickCategory(category) {
            await Promise.all([this.waitForSearchPreferenceSave(), this.recordSearch(this.searchValue)]);
            window.location.href = category.url;
        },

        // a history chip searches that term straight away
        searchHistoryTerm(term) {
            this.leavingForResults = true;
            this.searchValue = term;
            this.showResult = false;
            this.dropdownControl();
            this.goToSearchResults();
        },

        deactivateIfNeeded() {
            this.showSearch = false;
        },

        ignoreLoadingElement(event) {
            if (event.target.classList.contains("loading")) return false;
            return true;
        },

        closeDropdownIfClickedOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.showResult = false;
                this.editingSearchHistory = false;
                this.dropdownControl();
            }
        },

        // typing brings the suggestions back after a click outside closed them
        reopenDropdown() {
            if (!this.searchValue.trim()) return;

            this.showResult = true;
            this.dropdownControl();
        },

        dropdownControl() {
            // through Bootstrap so the menu is positioned again and its open state stays in sync
            if (window.$?.fn?.dropdown) {
                window.$(this.$refs.searchInput).dropdown(this.showResult ? "show" : "hide");
                return;
            }

            this.$refs.dropdownSearchBar.classList.toggle("show", this.showResult);
        },

        checkKeyPress(event) {
            if (event.key === "Enter") {
                this.goToSearchResults();
            }
        },

        // full results page; carries every selected field so it knows whether to show related categories
        async goToSearchResults() {
            if (this.searchValue.trim() == "") return;

            await Promise.all([this.waitForSearchPreferenceSave(), this.recordSearch(this.searchValue)]);

            var encodedSearchString = encodeURIComponent(encodeURIComponent(this.searchValue.trim()));
            var searchFieldString = this.selectedFields.map((field) => "luceneSearchField[]=" + field).join("&");

            window.location.href =
                this.searchResultUrl +
                "?luceneSearchString=" +
                encodedSearchString +
                "&" +
                searchFieldString +
                (this.categoryId ? "&categoryId=" + this.newCategoryId : "") +
                "&isCategorySearch=" +
                this.searchCategory;
        },

        toggleCategoryFilter() {
            if (!this.isHomePage) {
                this.searchCategory = !this.searchCategory;
                this.searchIfNeeded();
            }
        },
    },
};
</script>
